import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { Box, Spinner, Center } from '@chakra-ui/react';
import { isPartnerOnly } from '../lib/partnerNav';

const ProtectedRoute = ({ children, allowedForNonContract = false, requireAdmin = false }) => {
  const { userInfo, isLoading } = useUser();
  const location = useLocation();

  // 로딩 중일 때는 로딩 표시
  if (isLoading) {
    return (
      <Center h="100vh">
        <Box textAlign="center">
          <Spinner size="xl" color="brand.500" thickness="4px" />
        </Box>
      </Center>
    );
  }

  // 세션이 없거나 userInfo.email이 없으면 /login으로 리다이렉트.
  // 돌아올 주소(next)를 같이 넘긴다 — 메일·알림 링크로 들어온 사람이 로그인 뒤 홈이 아니라 그 페이지로 가게 (2026-09-13 QA).
  // 사이트 안 경로만 넘기고(바깥 주소 불가), Login 쪽 safeNextPath 가 한 번 더 검사한다. 권한 검사는 바뀌지 않는다.
  if (!userInfo.email) {
    const nextPath = `${location.pathname || ""}${location.search || ""}`;
    const next = nextPath.startsWith("/") && !nextPath.startsWith("//") && nextPath !== "/" ? `?next=${encodeURIComponent(nextPath)}` : "";
    return <Navigate to={`/login${next}`} replace />;
  }

  // Admin 권한 체크
  if (requireAdmin && !userInfo.role?.includes("Admin")) {
    return <Navigate to="/home" replace />;
  }

  // Platform role 체크 (플랫폼사 계정 — 카카오 등 외부 플랫폼 담당자)
  const isPlatformRole = userInfo.role?.includes("Platform");
  const isAdminRole = userInfo.role?.includes("Admin");
  const isOnPlatformPath = location.pathname.startsWith("/platform");
  // Platform role이면 /platform/* 만 접근 가능 (Partner 단일 페이지 잠금과 동일 패턴)
  if (isPlatformRole && !isAdminRole && !isOnPlatformPath) {
    return <Navigate to="/platform/kakao-dashboard" replace />;
  }
  // 반대로 /platform/* 은 Platform 또는 Admin 만 접근 가능
  if (isOnPlatformPath && !isPlatformRole && !isAdminRole) {
    return <Navigate to="/home" replace />;
  }

  // Partner role 체크 — 파트너 허브(/partner*) · 정산(/partner-settlement) · 내 정보·비밀번호 변경 · 문의·소명(/support, 2026-09-12 2단계 S-6)만. 그 외는 허브로 (2026-09-09 파트너 허브 1차)
  // /settings·/notice·/policies 는 열지 않는다 — 크리에이터 전용 기능(레슨 다시 보기·가점)과 대외 비공개 정책 전문이 있다 (QA 2026-09-09).
  // Admin 겸직은 잠그지 않는다(운영 확인용) — Platform 역할과 같은 규칙. Creator 겸직도 잠그지 않는다(2026-09-21 대표 결정 — 크리에이터 화면 그대로, /partner* 는 파트너 레이아웃).
  // 판정은 partnerNav.isPartnerOnly 한 곳 — 레이아웃(usesPartnerLayout)과 같은 기준을 본다.
  if (isPartnerOnly(userInfo.role)) {
    const partnerAllowed = ["/partner", "/partner-settlement", "/edit-profile", "/change-password", "/support"];
    const allowed = partnerAllowed.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
    if (!allowed) {
      return <Navigate to="/partner" replace />;
    }
  }

  // Copyrights Holder 전용 리다이렉트
  // role이 Copyrights Holder만 있을 경우 (Admin/Creator/Master/Manager 없음) /video/rights-manager로
  // Partner 단독(+Copyrights Holder)은 위 파트너 잠금이 먼저 /partner 로 보냈으므로 여기서 다시 권리사 화면으로 튕기지 않는다
  // (2026-09-21 — 종전엔 /partner ↔ /video/rights-manager 를 서로 넘기며 무한 리다이렉트).
  const onlyCopyrightsHolder =
    userInfo.role?.includes("Copyrights Holder") &&
    !userInfo.role?.includes("Admin") &&
    !userInfo.role?.includes("Creator") &&
    !userInfo.role?.includes("Master") &&
    !userInfo.role?.includes("Manager") &&
    !userInfo.role?.includes("Partner");
  const videoRightsPaths = ["/video/", "/video-dashboard", "/video-insight", "/video-episode-insight", "/video-channel-works", "/video-shortform-insight", "/total-copyrights", "/settings", "/edit-profile", "/change-password", "/notice", "/support"];
  const isOnVideoRightsPath = videoRightsPaths.some((p) => location.pathname.startsWith(p));
  if (onlyCopyrightsHolder && !isOnVideoRightsPath) {
    return <Navigate to="/video/rights-manager" replace />;
  }

  // is_not_contract가 true이고 allowedForNonContract가 false인 경우 /total로 리다이렉트
  // 이미 /total 경로면 자기 자신으로 무한 리다이렉트되어 흰 화면이 되므로 스킵
  if (userInfo?.is_not_contract && !allowedForNonContract && location.pathname !== "/total") {
    return <Navigate to="/total" replace />;
  }

  return children;
};

export default ProtectedRoute;
